import BaseFilterResponse from './base-filter-response';
import Butterworth from './butterworth';
import Bessel from './bessel';
import FilterConstants from '../../filter-constants';
import * as Complex from '../process-data/complex';
import * as StageUtils from '../utils/stage';

/**
Bessel Butterworth Low Pass Alogorithm
Created Jan 10, 2013 Matt Duff

This function creates a Bessel Butterworth transitional filter.

It first calculates the minimum Butterworth order required to meet the customer's requirements.  From this order, it then steps 
through the Bessel table, combining Butterworth and Bessel poles.   Once this table is created, it does a brute force
search for the minimum order that meets the customer's requirements - just like we do in the Bessel function.

Input Variables:
passbandAttenuation -     Passband attenuation in dB; always a negative number
stopbandAttenuation -     Stopband attenuation in dB; always a negative number
passbandFrequency   -     Passband frequency in Hz
stopbandFrequency   -     Stopband frequency in Hz
m       -     number between 0 and 1.   Sets ratio of Bessel to Butterworth.   0=all Butterworth  1=all Bessel

output is 2D array consisting of
- error object containing errorFlag and errorMessage (which are left blank to be set in a higher level function)
- array of stage information
*/

export default class ButterworthBessel extends BaseFilterResponse {
  bessel = Bessel.create();
  butterworth = Butterworth.create();
  name = FilterConstants.responseType.butterworthBessel;
  reverse2ndOrderStages(stages) {
    stages.reverse();
    var lastIndex = stages.length - 1;
    if (stages[lastIndex].orderType === 1) {
      var firstOrderStage = stages.pop();
      stages.unshift(firstOrderStage);
    }

    return stages;
  }

  /*This function combines 1D butterworth and bessel stages arrays into a 1D butterworthbessel array, according to the ratio, m */
  combineBesselandButterworthStage(butterworthStage, besselStage, butterworthBesselRatio) {
    var comboPoles = [];

    var complexButterworthInfluence, complexBesselInfluence;

    var complexButterworthPoles = this.fpAndQToPoles(butterworthStage);
    var complexBesselPoles = this.fpAndQToPoles(besselStage);

    for (var i = 0; i < complexButterworthPoles.length; i++) {
      complexButterworthInfluence = Complex.complexNumberRaisedToPower(
        complexButterworthPoles[i],
        butterworthBesselRatio,
      );
      complexBesselInfluence = Complex.complexNumberRaisedToPower(complexBesselPoles[i], 1 - butterworthBesselRatio);
      comboPoles[i] = complexButterworthInfluence.mult(complexBesselInfluence);
    }

    return this.polesToFpAndQ(butterworthStage, comboPoles);
  }

  /*This function takes in the fp and Q information in the stage array and converts it to an array of poles*/
  fpAndQToPoles(stage) {
    var wo = stage.stageFp * 2 * Math.PI;
    var complexPoles;
    var complexOne = Complex.createComplex(1, 0);
    var real;

    if (stage.orderType === 1) {
      complexPoles = [Complex.createComplex(wo, 0)];
    }

    if (stage.orderType === 2) {
      real = 1 - 4 * stage.stageQ * stage.stageQ;

      var complexUndersqrt = Complex.createComplex(real, 0);
      real = (-1 * wo) / (2 * stage.stageQ);
      var complexMultiplier = Complex.createComplex(real, 0);

      var complexRoot = Complex.complexNumberRaisedToPower(complexUndersqrt, 0.5); //compute square root
      var pole1 = complexMultiplier.mult(complexOne.subs(complexRoot));
      var pole2 = complexMultiplier.mult(complexOne.plus(complexRoot));
      complexPoles = [pole1, pole2];
    }

    return complexPoles;
  }

  /*This function takes an array of poles converts it to stage information
    Inputs:
    stagesTemplate: contains all stage information except for fp and Q (this function will overwrite the fp and Q)
    poles:   array of complex numbers representing the pole information
    Output:
    array of stage information
    */
  polesToFpAndQ(stage, poles) {
    var wo, fp, Q;

    if (poles.length === 1) {
      wo = poles[0].real;
      Q = stage.stageQ;
    }

    if (poles.length === 2) {
      var complexPolesProduct = poles[0].mult(poles[1]);
      var complexRoot = Complex.complexNumberRaisedToPower(complexPolesProduct, 0.5); //compute square root
      wo = complexRoot.magnitude();

      var complexPolesSum = poles[0].plus(poles[1]);
      var complexQuotient = complexRoot.divide(complexPolesSum);
      var complexZero = Complex.createComplex(0, 0);
      var complexNegated = complexZero.subs(complexQuotient);
      Q = complexNegated.magnitude();
    }

    fp = wo / (2 * Math.PI);
    stage.stageQ = Q;
    stage.stageFp = fp;

    return stage;
  }

  lowPass(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency, butterworthBesselRatio) {
    var stageGroup = this.getTargetStageGroup(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      butterworthBesselRatio,
    );
    var result = StageUtils.createTargetStages(stageGroup.stages);
    /*reverse 2nd order stage order so it matches LabVIEW.   Doesn't really matter, but should make the QA folks more comfortable.*/
    //lowPassFilterOutput[1].stages = reverse2ndOrderStages(lowPassFilterOutput[1].stages);
    /*Actually doesn't make a difference. Maybe distribute gain function changes order????*/

    return result;
  }

  getTargetStageGroup(
    passbandAttenuation,
    stopbandAttenuation,
    passbandFrequency,
    stopbandFrequency,
    butterworthBesselRatio,
  ) {
    var besselButterworthTable = this._getBesselButterworthTable(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      butterworthBesselRatio,
    );
    var orderDiv = this._findAppropriateOrder(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      besselButterworthTable,
    );
    var result = this.bessel.getTargetStageGroup(orderDiv, besselButterworthTable);
    return result;
  }

  _findAppropriateOrder(
    passbandAttenuation,
    stopbandAttenuation,
    passbandFrequency,
    stopbandFrequency,
    besselButterworthTable,
  ) {
    var orderDiv = this.bessel.findAppropriateOrder(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      besselButterworthTable,
    );
    return orderDiv;
  }

  _getBesselButterworthTable(
    passbandAttenuation,
    stopbandAttenuation,
    passbandFrequency,
    stopbandFrequency,
    butterworthBesselRatio,
  ) {
    var besselButterworthTable = [];

    var besselTable = this.bessel.createBesselTable(passbandFrequency);
    var tableLength = besselTable.length;

    //figure out Butterworth order and cutoff frequency
    var orderNum = this.butterworth.getOrder(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
    );
    var fp = this.butterworth.calculateButterworthPoleFrequency(passbandAttenuation, orderNum, passbandFrequency);

    var orderCounter = 0;
    var stages = [];

    //start counting at Butterworth order, since order of Bessel Butterworth will be this or higher
    while (orderCounter + orderNum <= tableLength && orderCounter + orderNum <= FilterConstants.maxOrder) {
      var currentOrder = orderCounter + orderNum;
      var butterworthStages = this.butterworth.butterworthStagesGivenOrderAndCutoff(currentOrder, fp);

      //now rearrange butterworth array so that lowest Q values in 2nd order functions, so it's just like the bessel array.
      butterworthStages = this.reverse2ndOrderStages(butterworthStages);

      //subtract 1 from our count, since order x in the table is at index x-1  (i.e. computer languages start counting from 0)
      var besselStages = besselTable[currentOrder - 1].stages;

      for (var i = 0; i < besselStages.length; i++) {
        // var besselJsonStageValues = besselStages[i];
        stages[i] = this.combineBesselandButterworthStage(
          butterworthStages[i],
          besselStages[i],
          butterworthBesselRatio,
        );
      }

      besselButterworthTable.push({ nthOrder: currentOrder, stages: stages.slice() });

      orderCounter++;
    }

    return besselButterworthTable;
  }

  getOrder(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency, butterworthBesselRatio) {
    var besselButterworthTable = this._getBesselButterworthTable(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      butterworthBesselRatio,
    );
    var orderDiv = this._findAppropriateOrder(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      besselButterworthTable,
    );
    return orderDiv.order;
  }
}
